/**
 * KAN-678: диалог правки окна отправки Telegram-рассылки.
 *
 * Поля: оператор (только если операторов больше одного), дни недели (KAN-764), начало и конец окна
 * (type=time), пауза между сообщениями от/до (секунды), лимит новых чатов в
 * день. Валидация - lib/outreachWindow.validateOutreachWindowForm; ошибка API
 * показывается текстом в диалоге.
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { WeekdaysPicker } from '@/components/common/WeekdaysPicker';
import {
  formFromValues,
  validateOutreachWindowForm,
  valuesFromForm,
  type NumberBounds,
  type OutreachWindowForm,
  type OutreachWindowValues,
} from '@/lib/outreachWindow';
import { zoneLabel } from '@/lib/outreachTimeline';
import type { OutreachOperator } from '@/hooks/useOutreachWindow';

export const SAVE_ERROR = 'Не удалось сохранить окно отправки';

interface OutreachWindowDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  operators: OutreachOperator[];
  selectedOperatorId: string | null;
  onOperatorChange: (id: string) => void;
  /** null, пока карточка выбранного оператора грузится. */
  values: OutreachWindowValues | null;
  limitBounds: NumberBounds;
  timezone: string;
  canEdit: boolean;
  isSaving: boolean;
  onSave: (values: OutreachWindowValues) => Promise<unknown>;
}

const EMPTY_FORM: OutreachWindowForm = {
  window_start: '',
  window_end: '',
  delay_min_s: '',
  delay_max_s: '',
  daily_new_chats_limit: '',
  send_days: [],
};

export function OutreachWindowDialog({
  open,
  onOpenChange,
  operators,
  selectedOperatorId,
  onOperatorChange,
  values,
  limitBounds,
  timezone,
  canEdit,
  isSaving,
  onSave,
}: OutreachWindowDialogProps): JSX.Element {
  const [form, setForm] = useState<OutreachWindowForm>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  // Форма заполняется из сохранённых значений при открытии и при смене оператора.
  useEffect(() => {
    if (!open) return;
    setForm(values ? formFromValues(values) : EMPTY_FORM);
    setError(null);
  }, [open, values, selectedOperatorId]);

  const setField = (
    name: Exclude<keyof OutreachWindowForm, 'send_days'>,
    value: string
  ): void => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const problem = validateOutreachWindowForm(form, limitBounds);
    setError(problem);
    if (problem) return;
    try {
      await onSave(valuesFromForm(form));
      toast.success('Окно отправки сохранено');
      onOpenChange(false);
    } catch (err) {
      setError(extractErrorMessage(err, SAVE_ERROR));
    }
  };

  const disabled = !canEdit || isSaving || values === null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="outreach-window-dialog">
        <form onSubmit={handleSubmit} noValidate>
          <DialogHeader>
            <DialogTitle>Окно отправки Telegram</DialogTitle>
            <DialogDescription>
              Время по поясу организации ({zoneLabel(timezone)}). Первые сообщения
              уходят только внутри окна, с паузой между ними и не больше лимита в день.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {operators.length > 1 && (
              <div className="space-y-1.5">
                <Label htmlFor="outreach-window-operator">Оператор</Label>
                <NativeSelect
                  id="outreach-window-operator"
                  data-testid="outreach-window-operator"
                  value={selectedOperatorId ?? ''}
                  onChange={(e) => onOperatorChange(e.target.value)}
                  options={operators.map((o) => ({ value: o.id, label: o.label }))}
                  disabled={isSaving}
                />
              </div>
            )}

            <div className="space-y-1.5">
              <Label>Дни рассылки</Label>
              <WeekdaysPicker
                idPrefix="outreach-window"
                testId="outreach-window-days"
                value={form.send_days}
                onChange={(days) => setForm((prev) => ({ ...prev, send_days: days }))}
                disabled={disabled}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="outreach-window-start">Писать с</Label>
                <Input
                  id="outreach-window-start"
                  data-testid="outreach-window-start"
                  type="time"
                  value={form.window_start}
                  onChange={(e) => setField('window_start', e.target.value)}
                  disabled={disabled}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="outreach-window-end">Писать до</Label>
                <Input
                  id="outreach-window-end"
                  data-testid="outreach-window-end"
                  type="time"
                  value={form.window_end}
                  onChange={(e) => setField('window_end', e.target.value)}
                  disabled={disabled}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="outreach-window-delay-min">Пауза от, с</Label>
                <Input
                  id="outreach-window-delay-min"
                  data-testid="outreach-window-delay-min"
                  type="number"
                  min={0}
                  step={1}
                  value={form.delay_min_s}
                  onChange={(e) => setField('delay_min_s', e.target.value)}
                  disabled={disabled}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="outreach-window-delay-max">Пауза до, с</Label>
                <Input
                  id="outreach-window-delay-max"
                  data-testid="outreach-window-delay-max"
                  type="number"
                  min={0}
                  step={1}
                  value={form.delay_max_s}
                  onChange={(e) => setField('delay_max_s', e.target.value)}
                  disabled={disabled}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="outreach-window-limit">Новых чатов в день</Label>
              <Input
                id="outreach-window-limit"
                data-testid="outreach-window-limit"
                type="number"
                min={limitBounds.min}
                max={limitBounds.max}
                step={1}
                value={form.daily_new_chats_limit}
                onChange={(e) => setField('daily_new_chats_limit', e.target.value)}
                disabled={disabled}
              />
              <p className="text-xs text-muted-foreground">
                Общий лимит канала; свой лимит аккаунта задаётся на странице аккаунтов.
              </p>
            </div>

            {error && (
              <p
                data-testid="outreach-window-error"
                role="alert"
                className="text-sm text-red-600"
              >
                {error}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
            >
              Отмена
            </Button>
            <Button
              type="submit"
              data-testid="outreach-window-save"
              disabled={disabled}
            >
              {isSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Сохранить
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
